/**
 * Portal Example - Main Application
 * Demonstrates WebApp with Portal layout (sidebar + topnav)
 */

import { PortalWebApp, User, UserForms, ConsoleSilencer, VERSION_INFO } from 'web-mojo';
import { registerAdminPages, registerAssistant } from 'web-mojo/admin';
import { registerLocationPlugin } from 'web-mojo/map';
import { registerGlobalPages } from './global/global_pages.js';
import { registerDevicePages } from './devices/devices_pages.js';
import { registerRoutePages } from './routes/routes_pages.js';
import { registerVendingPages } from './vending/vending_pages.js';
import { registerSharedPages } from './shared/shared_pages.js';
import { registerFirmwarePages } from './firmware/firmware_pages.js';
import config from '../config.js';
import { BUILD_COMMIT, VERSION } from '../version.js';

ConsoleSilencer.setLevel('debug');

// Create and configure the app
const app = new PortalWebApp({
    name: config.name,
    version: VERSION,
    debug: true,
    basePath: config.basePath,

    // Layout configuration
    layout: 'portal',
    container: '#app',
    pageContainer: '#page-container',

    // API configuration
    api: {
        baseUrl: config.apiURL,
        timeout: config.apiTimeout
    },

    // Auth — redirect to login on auth failure or logout
    auth: { loginUrl: config.authURL },

    // WebSocket — auto-connect to /ws/realtime/ after auth
    ws: true,

    // Default brand configuration
    brand: config.brand,
    brandIcon: config.brandIcon,

    // Sidebar configuration with one collapsible menu
    sidebar: {
        groupSelectorMode: 'dialog',
        defaultMenu: 'group_devices',
        menus: [{
           name: "system",
           className: 'sidebar sidebar-light sidebar-admin',
           header: "<div class='pt-3 text-center fs-5 fw-bold'><i class='bi bi-wrench pe-2'></i> System</div>",
           items: [
               {
                   spacer: true
               },
               {
                   text: 'Exit Admin',
                   action: 'exit_admin',
                   icon: 'bi-arrow-bar-left',
                   handler: async (action, event, el) => {;
                       if (app.activeGroup) {
                           // Perform actions specific to the active group
                           app.sidebar.showMenuForGroup(app.activeGroup);
                       } else {
                           app.sidebar.showGroupSearch();
                       }
                   }
               }
           ],
           footer: `
               <div class="text-center small p-2">
                    <div class="mt-1">App v${VERSION}</div>
                    <div class="mt-1">Build ${BUILD_COMMIT.slice(0, 12)}</div>
                   <div class="mt-1">Mojo v${VERSION_INFO.full}</div>
               </div>
           `
        }]
    },

    // Topbar configuration
    topbar: {
        brand: 'MOJOWARE',
        brandIcon: 'bi-lightning-charge',
        brandRoute: '?page=home',
        // theme: 'navbar-dark bg-primary',
        displayMode: 'both',
        showSidebarToggle: true,
        theme: "dark",
        shadow: "dark",
        rightItems: [
            {
                id: "tickets",
                icon: 'bi-ticket-detailed',
                tooltip: 'Tickets',
                action: 'ticket-menu',
                buttonClass: 'btn btn-link',
                permissions: ["view_tickets", "manage_users"],
                handler: async (action, event, el) => {
                    app.showPage("global/tickets");
                }
            },
            {
                id: "vending-menu",
                icon: 'bi-shop',
                action: 'vending-menu',
                buttonClass: 'btn btn-link text-white',
                permissions: ["view_payments", "manage_payments", "payments", "manage_group"],
                tooltip: "Vending Platform",
                handler: async (action, event, el) => {
                    app.sidebar.setActiveMenu("group_vending");
                    app.showPage("group/vending/machines");
                }
            },
            {
                id: "routes-menu",
                icon: 'bi-signpost-split-fill',
                action: 'routes-menu',
                buttonClass: 'btn btn-link text-white',
                permissions: ["routes", "view_routes", "manage_routes", "manage_group"],
                tooltip: "Routes Platform",
                handler: async (action, event, el) => {
                    app.sidebar.setActiveMenu("group_routes");
                    app.showPage("group/dashboard");
                }
            },
            {
                id: "devices-menu",
                icon: 'bi-lock-fill',
                action: 'devices-menu',
                buttonClass: 'btn btn-link text-white',
                permissions: ["manage_cam_devices", "manage_group"],
                tooltip: "Devices Platform",
                handler: async (action, event, el) => {
                    app.sidebar.setActiveMenu("group_devices");
                    app.showPage("group/device/dashboard");
                }
            },
            {
                id: "global",
                icon: 'bi-globe',
                action: 'global-menu',
                buttonClass: 'btn btn-link text-white',
                permissions: "view_global",
                tooltip: "Global Menu",
                handler: async (action, event, el) => {
                    console.log("GLOBAL CLICKED");
                    app.sidebar.setActiveMenu("global");
                }
            },
            {
                id: "admin",
                icon: 'bi-wrench',
                action: 'admin-menu',
                buttonClass: 'btn btn-link text-white',
                permissions: "view_admin",
                tooltip: "Admin Menu",
                handler: async (action, event, el) => {
                    console.log("ADMIN CLICKED");
                    app.sidebar.setActiveMenu("system");
                }
            },
            // Placeholder — TopNav.setUser() swaps this for `userMenu` once auth loads.
            // Required: without an `id: 'login'` anchor the user avatar/menu never appears.
            {
                id: "login",
                icon: 'bi-box-arrow-in-right',
                href: config.authURL,
                label: 'Login'
            }
        ],
        userMenu: {
            label: 'User',
            icon: 'bi-person-circle',
            items: [
                {
                    label: 'Profile',
                    icon: 'bi-person',
                    action: 'profile'
                },
                {
                    divider: true
                },
                {
                    label: 'Logout',
                    icon: 'bi-box-arrow-right',
                    action: 'logout'
                }
            ]
        }
    },

    // Default route
    defaultRoute: 'home'
});

window.app = app;
app.api = app.rest;

registerLocationPlugin();

app.events.on('ws:ready', () => {
    console.log('WebSocket connected');
});

app.events.on('ws:lost', () => {
    console.log('WebSocket disconnected');
});

// Attach app-specific WS message handlers once, after WS is available
app.events.once('ws:ready', () => {
    app.ws.on('message:auth_success', () => {
        if (app.activeGroup) {
            app.ws.send({ type: 'subscribe', topic: `group:${app.activeGroup.id}` });
        }
    });

    // Handle topic notifications (published via realtime.publish_topic)
    app.ws.on('message:message', (message) => {
        const data = message.data || message;
        console.log('WS notification:', data);

        switch (data.name) {
            case 'lock': {
                const name = data.lock_name || 'Device';
                // Dual-lock (ACS101-SHARPS-A) detection: trust board when reported;
                // the non-null right-lock fallback applies only while board
                // is blank, so a device re-flashed single-lock under the
                // same uuid reverts once its corrected board arrives.
                const isDual = data.board === 'ACS101-SHARPS-A' ||
                    (!data.board && data.lock_right_engaged != null);
                const toastLock = (label, engaged) => {
                    if (engaged) {
                        app.toast.error(`${label} locked`, {
                            title: "LOCKED",
                            icon: "bi bi-lock-fill",
                            autohide: true
                        });
                    } else {
                        app.toast.success(`${label} unlocked`, {
                            title: "UNLOCKED",
                            icon: "bi bi-unlock-fill"
                        });
                    }
                };
                // ACS101-E (S100): the banner tracks securement, not the
                // commanded latch — a bolt popping open under an engaged latch
                // is a warning ("OPEN"), never a reassuring green "UNLOCKED".
                const toastS100 = (message) => {
                    if (data.is_locked) {
                        app.toast.error(message, {
                            title: "LOCKED",
                            icon: "bi bi-lock-fill",
                            autohide: true
                        });
                    } else if (data.lock_engaged) {
                        // latch engaged, bolt open — not secured
                        app.toast.warning(message, {
                            title: "OPEN",
                            icon: "bi bi-door-open-fill"
                        });
                    } else {
                        // latch released
                        app.toast.success(message, {
                            title: "UNLOCKED",
                            icon: "bi bi-unlock-fill"
                        });
                    }
                };
                if (data.changed === undefined) {
                    // Legacy server without a changed list: every broadcast
                    // toasts, exactly as before.
                    toastLock(name, data.lock_engaged);
                } else {
                    // Toast only real transitions — presence-only broadcasts
                    // (connect/disconnect) carry [] and stay silent, and
                    // latch-only deltas no longer toast a mislabeled
                    // LOCKED/UNLOCKED from unchanged lock_engaged.
                    for (const field of data.changed) {
                        if (data.board === 'ACS101-E' && field === 'lock_engaged') {
                            toastS100(`${name} latch ${data.lock_engaged ? 'engaged' : 'released'}`);
                        } else if (data.board === 'ACS101-E' && field === 'latch_opened') {
                            toastS100(`${name} bolt ${data.latch_opened ? 'open' : 'closed'}`);
                        } else if (field === 'lock_engaged') {
                            toastLock(isDual ? `${name} left lock` : name, data.lock_engaged);
                        } else if (field === 'lock_right_engaged') {
                            toastLock(`${name} right lock`, data.lock_right_engaged);
                        } else if (field === 'bin_left_full' || field === 'bin_right_full') {
                            const side = field === 'bin_left_full' ? 'left' : 'right';
                            if (data[field]) {
                                app.toast.warning(`${name} ${side} bin full`, {
                                    title: "BIN FULL",
                                    icon: "bi bi-trash-fill"
                                });
                            } else {
                                app.toast.success(`${name} ${side} bin emptied`, {
                                    title: "BIN EMPTIED",
                                    icon: "bi bi-trash"
                                });
                            }
                        }
                    }
                }
                app.events.emit('device:state_changed', {
                    device_id: data.device_id,
                    lock_engaged: data.lock_engaged,
                    latch_opened: data.latch_opened,
                    is_locked: data.is_locked,
                    is_online: data.is_online,
                    board: data.board,
                    lock_version: data.lock_version,
                    lock_right_engaged: data.lock_right_engaged,
                    latch_right_opened: data.latch_right_opened,
                    bin_left_full: data.bin_left_full,
                    bin_right_full: data.bin_right_full,
                    bin_health: data.bin_health,
                    changed: data.changed,
                });
                break;
            }
            case 'lock_left_open':
                app.toast.warning(`${data.lock_name || 'Device'} left open`, {
                    title: "DOOR OPEN",
                    icon: "bi bi-door-open-fill"
                });
                break;
            case 'route_started':
            case 'route_completed':
            case 'stop_update':
            case 'stop_inserted':
            case 'stop_removed':
            case 'route_split':
            case 'operator_position':
            case 'operator_off_route':
            case 'exception_update':
                app.events.emit(`route:${data.name}`, data);
                break;
            case 'access_window_updated':
                app.events.emit('access_window:updated', data);
                break;
            case 'device:ota_progress':
                app.events.emit('device:ota_progress', data);
                break;
            default:
                console.log('WS notification (unhandled):', data);
        }
    });

    app.ws.on('message', (message) => {
        console.log('WS raw:', message);
    });
});


app.events.on("group:loaded", (event) => {
    app.group = event.group || null;
    console.log('[portal] group:loaded', event.group, 'id=', event.group?.id, 'get(id)=', event.group?.get?.('id'));
});

app.events.on("group:changed", (event) => {
    app.group = event.group || null;

    const page = app.getCurrentPage();
    if (page.pageName.startsWith('group')) {
        // if (page && page.onGroupChange) {
        //     page.onGroupChange(event.group);
        // }
    } else {
        app.showPage("group/dashboard");
    }

    // handle websocket subscriptions
    if (app.ws?.isConnected) {
        if (event.previousGroup) {
            app.ws.send({type: 'unsubscribe', topic: `group:${event.previousGroup.id}`});
        }
        if (event.group) {
            app.ws.send({type: 'subscribe', topic: `group:${event.group.id}`});
        }
    }

});



// Initialize and start the application
(async () => {
    // Register pages automatically
    await registerGlobalPages(app);
    await registerSharedPages(app);
    await registerDevicePages(app);
    await registerRoutePages(app);
    await registerVendingPages(app);
    await registerFirmwarePages(app);

    // Register admin pages + AI Assistant topbar
    try {
        registerAdminPages(app, true);
        registerAssistant(app);
        console.log('Admin pages registered successfully');
    } catch (error) {
        console.warn('Failed to register admin pages:', error);
    }

    User.PERMISSIONS = [
        ...User.PERMISSIONS,
        // Devices (server-enforced)
        { name: "view_cam_devices", label: "View Devices" },
        { name: "manage_cam_devices", label: "Manage Devices (unlock bypass)" },
        { name: "provision_locks", label: "Provision Locks" },
        { name: "manage_firmware", label: "Manage Firmware" },
        // Routes & Operations (server-enforced)
        { name: "routes", label: "Routes — Full Access" },
        { name: "view_routes", label: "View Routes" },
        { name: "manage_routes", label: "Manage Routes" },
        // Payments & Vending (server-enforced)
        { name: "view_payments", label: "View Payments" },
        { name: "manage_payments", label: "Manage Payments" },
        { name: "payments", label: "Payments — Full Access" },
        // Portal-only UI gates (not server-enforced)
        { name: "view_metrics", label: "View Analytics" },
        { name: "billing", label: "Billing" },
        // App behaviour flags
        { name: "no_mobile_sync", label: "No Mobile Sync" },
        { name: "ignore_left_open", label: "Ignore Left Open Alerts" },
        { name: "ignore_on_portal", label: "Ignore Notifications in Portal" },
        // Knowledge & docs
        { name: "view_wiki", label: "View Knowledge Base" },
        { name: "edit_wiki", label: "Edit Knowledge Base" },
        { name: "view_wiki_apis", label: "API Docs" },
        { name: "view_camactive_service", label: "Service Docs" },
        // Misc
        { name: "view_beta", label: "View Beta Features" },
        { name: "sdk_access", label: "SDK Access" },
    ]

    UserForms.permissions.fields = [
        ...User.PERMISSIONS.map(permission => ({
            name: `permissions.${permission.name}`,
            type: 'switch',
            label: permission.label,
            columns: 4
        }))
    ]

    // Convenience: get the current group's numeric ID regardless of whether
    // app.group is a plain object or a web-mojo Model instance.
    app.groupId = () => {
        // Try framework's getActiveGroup() first (returns plain object with .id)
        const active = app.getActiveGroup?.();
        if (active?.id) return active.id;
        const g = app.group ?? app.activeGroup;
        // Handle Model (.get()), plain object (.id), or membership wrapper (.group.id)
        return g?.get?.('id') ?? g?.id ?? g?.group?.id ?? null;
    };

    // Start the application — auth, WS, and router are handled automatically
    app.start().then((result) => {
        console.log('Portal app started:', result.success ? 'authenticated' : 'auth failed');
        if (result.success) {
            app.group = app.activeGroup;
        }

        // Hide the initial loader once the app is ready
        if (window.hideInitialLoader) {
            window.hideInitialLoader();
        }
    }).catch(error => {
        console.error('Failed to start app:', error);
        // Also hide loader on error
        if (window.hideInitialLoader) {
            window.hideInitialLoader();
        }
    });
})();
